import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Candidates list
  candidatesList: [], // All sourced candidates

  // Selection management
  selectedCandidateIds: [], // Array of selected candidate IDs

  // Filtering and search
  filters: {
    searchTerm: '', // Search by name, email, phone
    skillMatchMin: null, // Minimum skill match percentage
    interviewStatus: null, // Filter by interview status: pending, in-progress, completed, failed
    hasAIScore: null, // Filter by AI score availability
  },

  // Sorting
  sortBy: 'search_rank', // search_rank, yrs_of_experience, skill_match, ai_score, name
  sortOrder: 'asc', // asc, desc

  // State flags
  isLoading: false,
  error: null,
};

const candidatesSlice = createSlice({
  name: 'candidates',
  initialState,
  reducers: {
    // Set candidates list
    setCandidatesList: (state, action) => {
      state.candidatesList = action.payload;
      state.error = null;
    },

    // Selection management - Single candidate
    selectCandidate: (state, action) => {
      const candidateId = action.payload;
      state.selectedCandidateIds = [candidateId];
    },

    // Selection management - Multiple candidates
    toggleCandidateSelection: (state, action) => {
      const candidateId = action.payload;
      const index = state.selectedCandidateIds.indexOf(candidateId);
      if (index > -1) {
        state.selectedCandidateIds.splice(index, 1);
      } else {
        state.selectedCandidateIds.push(candidateId);
      }
    },

    selectMultipleCandidates: (state, action) => {
      state.selectedCandidateIds = action.payload;
    },

    selectAllCandidates: (state) => {
      state.selectedCandidateIds = state.candidatesList.map((c) => c.candidate_id);
    },

    clearSelection: (state) => {
      state.selectedCandidateIds = [];
    },

    // Filtering actions
    setSearchTerm: (state, action) => {
      state.filters.searchTerm = action.payload;
    },

    setSkillMatchFilter: (state, action) => {
      state.filters.skillMatchMin = action.payload;
    },

    setInterviewStatusFilter: (state, action) => {
      state.filters.interviewStatus = action.payload;
    },

    setHasAIScoreFilter: (state, action) => {
      state.filters.hasAIScore = action.payload;
    },

    clearFilters: (state) => {
      state.filters = {
        searchTerm: '',
        skillMatchMin: null,
        interviewStatus: null,
        hasAIScore: null,
      };
    },

    // Sorting actions
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },

    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },

    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';
    },

    // State management
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    // Reset
    resetCandidates: (state) => {
      return initialState;
    },

    // Update candidate in list (e.g., after interview status change)
    updateCandidate: (state, action) => {
      const { candidateId, updates } = action.payload;
      const candidate = state.candidatesList.find((c) => c.candidate_id === candidateId);
      if (candidate) {
        Object.assign(candidate, updates);
      }
    },

    // Update multiple candidates
    updateCandidates: (state, action) => {
      const updates = action.payload; // Array of { candidateId, updates }
      updates.forEach(({ candidateId, updates: candidateUpdates }) => {
        const candidate = state.candidatesList.find((c) => c.candidate_id === candidateId);
        if (candidate) {
          Object.assign(candidate, candidateUpdates);
        }
      });
    },
  },
});

export const {
  setCandidatesList,
  selectCandidate,
  toggleCandidateSelection,
  selectMultipleCandidates,
  selectAllCandidates,
  clearSelection,
  setSearchTerm,
  setSkillMatchFilter,
  setInterviewStatusFilter,
  setHasAIScoreFilter,
  clearFilters,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  setIsLoading,
  setError,
  resetCandidates,
  updateCandidate,
  updateCandidates,
} = candidatesSlice.actions;

// Basic selectors
export const selectCandidatesList = (state) => state.candidates.candidatesList;
export const selectSelectedCandidateIds = (state) => state.candidates.selectedCandidateIds;
export const selectSelectedCandidatesCount = (state) => state.candidates.selectedCandidateIds.length;
export const selectFilters = (state) => state.candidates.filters;
export const selectSortBy = (state) => state.candidates.sortBy;
export const selectSortOrder = (state) => state.candidates.sortOrder;
export const selectIsLoading = (state) => state.candidates.isLoading;
export const selectError = (state) => state.candidates.error;

// Get selected candidates full data
export const selectSelectedCandidates = (state) => {
  const selectedIds = state.candidates.selectedCandidateIds;
  return state.candidates.candidatesList.filter((candidate) =>
    selectedIds.includes(candidate.candidate_id)
  );
};

// Get single candidate by ID
export const selectCandidateById = (candidateId) => (state) => {
  return state.candidates.candidatesList.find((c) => c.candidate_id === candidateId);
};

// Filter candidates by search term
export const selectSearchFilteredCandidates = (state) => {
  const { searchTerm } = state.candidates.filters;
  if (!searchTerm) {
    return state.candidates.candidatesList;
  }

  const term = searchTerm.toLowerCase();
  return state.candidates.candidatesList.filter(
    (candidate) =>
      candidate.full_name.toLowerCase().includes(term) ||
      candidate.email.toLowerCase().includes(term) ||
      candidate.contact_number.includes(term)
  );
};

// Filter candidates by skill match
export const selectSkillMatchFilteredCandidates = (state) => {
  const { skillMatchMin } = state.candidates.filters;
  if (!skillMatchMin) {
    return selectSearchFilteredCandidates(state);
  }

  return selectSearchFilteredCandidates(state).filter((candidate) => {
    const matched = candidate.skill_match?.must_have?.matched_count || 0;
    const total = candidate.skill_match?.must_have?.matched_count +
      candidate.skill_match?.must_have?.unmatched?.length || 0;
    const percentage = total > 0 ? (matched / total) * 100 : 0;
    return percentage >= skillMatchMin;
  });
};

// Filter candidates by interview status
export const selectInterviewStatusFilteredCandidates = (state) => {
  const { interviewStatus } = state.candidates.filters;
  const filtered = selectSkillMatchFilteredCandidates(state);

  if (!interviewStatus) {
    return filtered;
  }

  return filtered.filter((candidate) => candidate.interview_status === interviewStatus);
};

// All filters combined
export const selectFilteredCandidates = (state) => {
  const { hasAIScore } = state.candidates.filters;
  let filtered = selectInterviewStatusFilteredCandidates(state);

  if (hasAIScore !== null) {
    filtered = filtered.filter((candidate) => {
      if (hasAIScore) {
        return candidate.is_score_available === true;
      } else {
        return candidate.is_score_available === false;
      }
    });
  }

  return filtered;
};

// Sort candidates
export const selectSortedCandidates = (state) => {
  const candidates = selectFilteredCandidates(state);
  const { sortBy, sortOrder } = state.candidates;

  const sorted = [...candidates].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'name':
        aValue = a.full_name.toLowerCase();
        bValue = b.full_name.toLowerCase();
        break;
      case 'yrs_of_experience':
        aValue = a.yrs_of_experience || 0;
        bValue = b.yrs_of_experience || 0;
        break;
      case 'skill_match':
        aValue = a.skill_match?.must_have?.matched_count || 0;
        bValue = b.skill_match?.must_have?.matched_count || 0;
        break;
      case 'ai_score':
        aValue = a.ai_score || 0;
        bValue = b.ai_score || 0;
        break;
      case 'search_rank':
      default:
        aValue = a.search_rank || 999;
        bValue = b.search_rank || 999;
        break;
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });

  return sorted;
};

// Get final displayable candidates list (filtered + sorted)
export const selectDisplayCandidates = (state) => selectSortedCandidates(state);

// Count selected by interview status
export const selectSelectedByInterviewStatus = (state) => {
  const selected = selectSelectedCandidates(state);
  const count = {
    pending: 0,
    'in-progress': 0,
    completed: 0,
    failed: 0,
  };

  selected.forEach((candidate) => {
    const status = candidate.interview_status || 'pending';
    if (status in count) {
      count[status]++;
    }
  });

  return count;
};

// Check if mode should be single or bulk
export const selectInterviewMode = (state) => {
  const count = state.candidates.selectedCandidateIds.length;
  if (count === 0) return null;
  if (count === 1) return 'single';
  return 'bulk';
};

// Get count statistics
export const selectCandidatesStats = (state) => {
  const all = state.candidates.candidatesList;
  const displayed = selectDisplayCandidates(state);
  const selected = state.candidates.selectedCandidateIds.length;

  return {
    total: all.length,
    displayed: displayed.length,
    selected: selected,
    pending: all.filter((c) => c.interview_status === 'pending').length,
    inProgress: all.filter((c) => c.interview_status === 'in-progress').length,
    completed: all.filter((c) => c.interview_status === 'completed').length,
    failed: all.filter((c) => c.interview_status === 'failed').length,
  };
};

export default candidatesSlice.reducer;
